import { z } from 'zod';
import { MOTORES, ProvedorOpencodeSchema } from '@remoto/protocolo';
import { ContaNanogptSchema } from '../opencode/modelos';

import { EstadoSessaoSchema, FuncaoSessaoSchema } from '../sessoes';

export * from './precos';
export * from './rateio';

/**
 * Quanto o painel consumiu, por dia.
 *
 * A fonte é o que já está gravado: todo evento `assistant` do transcript
 * carrega `message.usage` e `message.model`, e a esteira de áudio já conta os
 * segundos cobrados por gravação. Nada disto vira coluna nova — é agregação no
 * service, como manda a regra da casa.
 *
 * A janela tem teto de 180 dias porque a retenção apaga eventos de sessões
 * encerradas depois disso: oferecer 365 seria oferecer um buraco.
 */
export const UsoQuerySchema = z.object({
  dias: z.coerce.number().int().min(1).max(180).default(30),
});
export type UsoQuery = z.infer<typeof UsoQuerySchema>;

/** Teto da janela, para a tela dizer de onde os números vêm. */
export const JANELA_MAXIMA_DIAS = 180;

export type UsoTokensDia = {
  /** `YYYY-MM-DD`, no fuso do servidor. */
  dia: string;
  modelo: string;
  entrada: number;
  saida: number;
  cacheLeitura: number;
  cacheEscrita: number;
};

export type UsoAudioDia = {
  dia: string;
  modeloStt: string;
  segundos: number;
  /** Aqui há preço de verdade (a Groq cobra por hora de áudio), então há USD. */
  usd: number;
};

export type UsoResumoDeIa = {
  modelo: string;
  tokensEntrada: number;
  tokensSaida: number;
  quantidade: number;
};

/**
 * Não há custo em dólar dos tokens do Claude de propósito: a conta é uma
 * assinatura Max, onde ninguém paga por token — o que limita é a cota. Uma
 * tabela de preços por modelo aqui seria manutenção pura para um número que
 * não corresponde a nenhuma fatura.
 */
export type UsoResumo = {
  janelaDias: number;
  desde: string;
  tokensPorDia: UsoTokensDia[];
  audioPorDia: UsoAudioDia[];
  resumos: UsoResumoDeIa[];
};

/*
  ─── ADR-0021: a tela de Uso estilo /usage ───────────────────────────────────

  Dois endpoints: `GET /uso/painel` (a tela inteira) e `GET /uso/sessoes` (só o
  % e o contexto de algumas sessões, para o cabeçalho da conversa e o selo da
  lista). O `pctSemanal` é ESTIMATIVA por rateio (`rateio.ts`): o serviço conta
  o uso da conta, nunca por sessão, e a tela tem de dizer isso.
*/

const Iso = z.iso.datetime();
const Fracao = z.number().min(0).max(1);
const MotorDaConta = z.enum(MOTORES);

/**
 * Uma janela de cota, dos dois motores na mesma forma. Ids: `claude:5h`,
 * `claude:7d`, `claude:7d-opus`, `claude:7d-sonnet`, `claude:modelo:<rotulo>`,
 * e os do Codex como já gravados (`<limitId>:primary|secondary`).
 */
export const JanelaDoPainelSchema = z.object({
  id: z.string().min(1),
  rotulo: z.string(),
  utilizacao: Fracao,
  reiniciaEm: Iso.nullable(),
  duracaoMin: z.number().int().positive().nullable(),
});
export type JanelaDoPainel = z.infer<typeof JanelaDoPainelSchema>;

/** Uma máquina que usa a conta agora, e em qual perfil (Codex: mestre ou trabalho). */
export const MaquinaDaContaSchema = z.object({
  nome: z.string(),
  perfil: z.enum(['mestre', 'trabalho']).nullable(),
});
export type MaquinaDaConta = z.infer<typeof MaquinaDaContaSchema>;

export const ContaDoPainelSchema = z.object({
  /** `<MOTOR>:<email>`, ou `<MOTOR>:?` para o uso gravado antes de a máquina dizer a conta (`idDaConta`). */
  id: z.string().min(1),
  motor: MotorDaConta,
  /** `null`: a conta não identificada — uso anterior ao carimbo, sem cota a ratear. */
  email: z.string().nullable(),
  /** As máquinas (visíveis a quem pergunta) logadas nesta conta AGORA. Vazia: nenhuma, só histórico. */
  maquinas: z.array(MaquinaDaContaSchema),
  plano: z.string().nullable(),
  atualizadoEm: Iso.nullable(),
  janelas: z.array(JanelaDoPainelSchema),
  /**
   * O total da SEMANA da conta em US$ — o custo REAL, somado do `custoUsd`
   * que o motor relatou (D20). Só o OPENCODE o tem: ele não tem cota, tem
   * crédito pago por token, e o relatado é o cobrado (R6).
   *
   * Opcional e SEM `.default()`: ausente é "não se sabe" (nenhuma chamada
   * disse quanto custou, ou quem pergunta não alcança máquina com gasto) —
   * um zero por padrão diria "saiu de graça", e no Claude e no Codex diria que
   * existe um total em dólar onde só existe cota.
   */
  totalUsdSemana: z.number().nonnegative().optional(),
  /**
   * Só no OPENCODE (F2): o provedor ATIVO da frota. É ele que diz como ler o
   * `totalUsdSemana` — no OpenRouter é o custo REAL; no NanoGPT é o
   * EQUIVALENTE de tabela (D45), porque a chamada coberta pela assinatura sai
   * de graça e o OpenCode relata o preço mesmo assim (medido em 26/10).
   * Opcional e sem `.default()`: ausente é a API anterior à F2.
   */
  provedorOpencode: ProvedorOpencodeSchema.optional(),
  /**
   * Só no OPENCODE com o NanoGPT ativo (D43, P3): a assinatura, o saldo e o
   * gasto em CRÉDITO da semana, lidos da conta. Dois totais no cartão, e não
   * uma cota inventada: o OpenCode continua fora de `temCotaDeAssinatura`.
   * Ausente: o provedor não é o NanoGPT, ou a chave não está guardada.
   */
  contaNanogpt: ContaNanogptSchema.optional(),
});
export type ContaDoPainel = z.infer<typeof ContaDoPainelSchema>;

export const SessaoDoPainelSchema = z.object({
  sessaoId: z.string().min(1),
  /**
   * A conta cujo uso esta linha soma. Uma sessão que atravessou uma troca de
   * conta aparece UMA VEZ POR CONTA, cada linha com o % da sua.
   */
  contaId: z.string().min(1),
  titulo: z.string(),
  maquina: z.string(),
  motor: MotorDaConta,
  funcao: FuncaoSessaoSchema,
  projeto: z.string().nullable(),
  modelo: z.string().nullable(),
  esforco: z.string().nullable(),
  estado: EstadoSessaoSchema,
  chamadas: z.number().int().nonnegative(),
  /**
   * O custo da sessão na janela, em US$, e o `motor` da linha diz como ler:
   * - **CLAUDE/CODEX**: o EQUIVALENTE pela tabela de preços (`pesoDoUso`), e
   *   só com preço conhecido do modelo — a conta é por cota, ninguém paga isto;
   * - **OPENCODE**: o custo REAL, a soma do `custoUsd` que o OpenRouter
   *   relatou (D20, R6) — nunca estimado. A tela escreve "custo real".
   * `null` quando não se sabe.
   */
  custoEquivUsd: z.number().nonnegative().nullable(),
  /**
   * D45: o modelo da sessão é do NanoGPT e está INCLUÍDO na assinatura — o
   * `custoEquivUsd` é o equivalente de tabela, e a tela põe o selo
   * "assinatura" ao lado. Opcional e sem `.default()`: ausente é "não se
   * aplica ou não se sabe" (o catálogo não respondeu).
   */
  assinatura: z.boolean().optional(),
  peso: z.number().nonnegative(),
  /** ESTIMATIVA por rateio. `null` quando a conta do motor não tem cota conhecida. */
  pctSemanal: Fracao.nullable(),
  contextoTokens: z.number().int().nonnegative().nullable(),
  contextoLimite: z.number().int().positive().nullable(),
  ultimaAtividadeEm: Iso.nullable(),
});
export type SessaoDoPainel = z.infer<typeof SessaoDoPainelSchema>;

/** Resposta de `GET /api/uso/painel`. */
export const UsoPainelSchema = z.object({
  geradoEm: Iso,
  contas: z.array(ContaDoPainelSchema),
  janelaSemanal: z.array(z.object({ motor: MotorDaConta, contaId: z.string().min(1), inicio: Iso, fim: Iso })),
  sessoes: z.array(SessaoDoPainelSchema),
});
export type UsoPainel = z.infer<typeof UsoPainelSchema>;

/** Teto de ids por consulta de `GET /uso/sessoes`: uma página da lista cabe, a frota inteira não. */
export const TETO_IDS_USO_SESSOES = 100;

/**
 * De quanto em quanto tempo o painel pergunta o uso (`/uso/painel` e
 * `/uso/sessoes`) — e a validade do que a API guarda dos grupos de uso. Um
 * número só, nos dois lados: o cache de 30 s da API errava quase sempre contra
 * a pergunta de 60 s do painel, e cada erro custava a consulta inteira
 * (20–46 s na produção de 2026-10-26).
 */
export const INTERVALO_DO_USO_MS = 60_000;

/** Forma de id do Prisma (`cuid()`): recusa lixo antes de ele virar `IN (...)`. */
const IdDeSessao = z.string().regex(/^c[a-z0-9]{20,40}$/, 'id_invalido');

/** Query de `GET /api/uso/sessoes?ids=a,b,c`. Chega string; sai lista sem repetição. */
export const UsoSessoesQuerySchema = z.object({
  ids: z
    .string()
    .min(1)
    .transform((s) => [...new Set(s.split(',').map((id) => id.trim()))])
    .pipe(z.array(IdDeSessao).min(1).max(TETO_IDS_USO_SESSOES)),
});
export type UsoSessoesQuery = z.infer<typeof UsoSessoesQuerySchema>;

export const UsoDaSessaoSchema = z.object({
  pctSemanal: Fracao.nullable(),
  /** A conta do `pctSemanal`: a do último uso da sessão. `null`: conta não identificada. */
  email: z.string().nullable(),
  contextoTokens: z.number().int().nonnegative().nullable(),
  contextoLimite: z.number().int().positive().nullable(),
  custoEquivUsd: z.number().nonnegative().nullable(),
  /** O mesmo selo da linha do painel (D45). */
  assinatura: z.boolean().optional(),
});
export type UsoDaSessao = z.infer<typeof UsoDaSessaoSchema>;

/**
 * Resposta de `GET /api/uso/sessoes`. Id fora do escopo ou inexistente
 * simplesmente NÃO aparece no mapa — nem como `null`, que diria "existe".
 */
export const UsoSessoesSchema = z.record(z.string(), UsoDaSessaoSchema);
export type UsoSessoes = z.infer<typeof UsoSessoesSchema>;

export { EXEMPLO_USO_PAINEL, EXEMPLO_USO_SESSOES } from './exemplos';
export { contaVigente, idDaConta } from './contas';
